import AutoCompleteParticipantModal from '@/components/modals/autocomplete-participant-modal/AutoCompleteParticipantModal';
import CalendarModal from '@/components/modals/calendar-modal/CalendarModal';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import useMixPanel from '@/hooks/useMixPanel';
import { useRootStore } from '@/providers/root-store-provider';
import { TUserData } from '@/services/api/user-api';
import { StepService } from '@/stores/slices/step-service/step-services.types';
import {
  maxHeightInputValidation,
  maxWeightInputValidation
} from '@/utils/utils';
import React, { useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type Props = {
  form: UseFormReturn<any, any, undefined>;
  user: TUserData | undefined;
  index: number;
  isMinParticipants: boolean;
  onSave: () => Promise<void>;
  removeParticipant: () => void;
};

const ParticipantForm = ({
  form,
  user,
  index,
  isMinParticipants,
  onSave,
  removeParticipant
}: Props) => {
  const stepServicesData: StepService | null = useRootStore(
    (store) => store.stepServicesData
  );
  const { t } = useTranslation(['newBooking', 'common']);
  const Mixpanel = useMixPanel();

  const formValue = form.getValues().participants[index];
  const participantFormValue = form.getValues().participants;

  const participantAvailable = user?.consumers?.filter(
    (consumer) =>
      !participantFormValue.some(
        (participant: any) => participant.id === consumer.id
      )
  );
  const addConsumerData = useRootStore((store) => store.addConsumerData);
  const cleanConsumers = useRootStore((store) => store.cleanConsumers);

  const isSelectedExistingParticipant = Boolean(formValue?.id);
  const disabledInputs = isSelectedExistingParticipant;
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState<string>('');

  const setExistingParticipant = (id: string) => {
    const participant = user?.consumers?.find((consumer) => consumer.id === id);
    if (participant) {
      const participantFormValue = {
        id: participant.id,
        firstName: participant.firstName,
        lastName: participant.lastName,
        birthdate: new Date(participant.birthdate),
        height: participant.height,
        weight: participant.weight
      };
      form.setValue(`participants.${index}`, participantFormValue);
    }

    onSave();
  };

  const removeSelectedParticipant = async () => {
    form.setValue(`participants.${index}`, {
      firstName: '',
      lastName: '',
      birthdate: new Date(),
      height: 0,
      weight: 0,
      id: ''
    });
    await onSave();
    form.clearErrors();
  };
  const handleSelect = (selectedParticipant: string) => {
    if (selectedParticipant) setExistingParticipant(selectedParticipant);
    setIsOpenDialog(false);
  };

  const handleValidate = () => {
    onSave();
    const data = form.getValues();
    const participantsData = data.participants.map(
      (participant: any, index: number) => {
        const { id, ...rest } = participant;
        if (id) return id;
        return {
          ...rest,
          birthdate: participant?.birthdate?.toISOString(),
          index
        };
      }
    );
    cleanConsumers('participants');
    addConsumerData(
      participantsData
        .filter((participant: any) => {
          return participant.firstName !== '';
        })
        .map((participant: any) => {
          return participant.id === index ? { ...participant } : participant;
        })
    );
  };

  useEffect(() => {
    if (
      stepServicesData?.consumers !== undefined &&
      stepServicesData?.consumers?.length > 0
    ) {
      stepServicesData?.consumers.map((consumer: any) => {
        if (!consumer.isOwnUser) {
          form.setValue(`participants.${consumer.index}`, {
            firstName: consumer.firstName,
            lastName: consumer.lastName,
            birthdate: new Date(consumer.birthdate),
            height: consumer.height,
            weight: consumer.weight
          });
        }
      });
    }
  }, []);

  return (
    <div className="border border-primary p-6 rounded-md flex flex-col gap-5">
      <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-center">
        <p className="text-sm font-normal text-primary">
          {stepServicesData?.service?.name}
        </p>
        <AutoCompleteParticipantModal
          open={isOpenDialog}
          onOpenChange={setIsOpenDialog}
          consumers={participantAvailable}
          selectedParticipant={selectedParticipant}
          setSelectedParticipant={setSelectedParticipant}
          handleSelect={handleSelect}
          removeSelectedParticipant={removeSelectedParticipant}
          formValue={formValue}
        />
      </div>
      <FormField
        control={form.control}
        name={`participants.${index}.firstName`}
        render={({ field }) => {
          return (
            <FormItem>
              <FormLabel>{t('stepParticipants.form.firstName')}</FormLabel>
              <FormControl>
                <Input
                  disabled={disabledInputs}
                  onClick={() =>
                    Mixpanel.track(
                      `press participants array[${index}]  first name input`,
                      {
                        screen: 'New Booking'
                      }
                    )
                  }
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      />
      <FormField
        control={form.control}
        name={`participants.${index}.lastName`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('stepParticipants.form.lastName')}</FormLabel>
            <FormControl>
              <Input
                disabled={disabledInputs}
                onClick={() =>
                  Mixpanel.track(
                    `press participants array[${index}]  last name input`,
                    {
                      screen: 'New Booking'
                    }
                  )
                }
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name={`participants.${index}.birthdate`}
        render={({ field }) => (
          <FormItem className="flex flex-col ">
            <FormLabel>{t('stepParticipants.form.birth')}</FormLabel>
            <CalendarModal
              value={field.value}
              disabledInputLabel={disabledInputs}
              onClickInputLabel={() =>
                Mixpanel.track(
                  `press participants array[${index}] birthday day button`,
                  {
                    screen: 'New Booking'
                  }
                )
              }
              onAccept={(value) => {
                field.onChange(value);
                Mixpanel.track(
                  `select date from  participants array[${index}] birthday calendar `,
                  {
                    screen: 'New Booking'
                  }
                );
              }}
              initialFocus
              dropDownPicker
            />
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name={`participants.${index}.weight`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('stepParticipants.form.weight')}</FormLabel>
            <FormControl>
              <Input
                disabled={disabledInputs}
                onClick={() =>
                  Mixpanel.track(
                    `press participants array[${index}] weight input`,
                    {
                      screen: 'New Booking'
                    }
                  )
                }
                type="number"
                {...field}
                onChange={(e) => {
                  if (maxWeightInputValidation(Number(e.target.value))) {
                    field.onChange(e);
                  }
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name={`participants.${index}.height`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('stepParticipants.form.height')}</FormLabel>
            <FormControl>
              <Input
                disabled={disabledInputs}
                onClick={() =>
                  Mixpanel.track(
                    `press participants array[${index}] height input`,
                    {
                      screen: 'New Booking'
                    }
                  )
                }
                step="1"
                type="number"
                {...field}
                onChange={(e) => {
                  if (maxHeightInputValidation(Number(e.target.value))) {
                    field.onChange(e);
                  }
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Button
          type="button"
          variant="outline"
          disabled={isMinParticipants}
          onClick={removeParticipant}
        >
          Remover participante
        </Button>
        <Button type="button" onClick={handleValidate}>
          {t('common:validate')}
        </Button>
      </div>
    </div>
  );
};

export default ParticipantForm;
